'use strict';
import classNames from 'classnames';
import isFunction from 'lodash/isFunction';
import memoize from 'lodash/memoize';
import * as T from 'prop-types';
import React, {Component} from 'react';
import {ReplaySubject} from 'rxjs/ReplaySubject';
import {getDomainClass, getId} from '../../../../core-utilities/domain/ReduxUtils';
import {ASSET_PROJECT_COLLECTION} from '../../../../projects/domain/AssetProjectDomain';
import {PROJECT_COLLECTION} from '../../../../projects/domain/ProjectDomain';
import {getStatusIcon} from '../../../../projects/services/TaskStatusService';
import {TaskStatus} from '../../../../tasks/enums/TaskStatus';
import {PopoverSelectComponent} from '../../../popover-select/PopoverSelectComponent';
import {SelectOption} from '../../../popover-select/SelectOption';
import statusRejectionTemplate from '../../status-selector-directive/status-rejection-modal.html';


// Mirror impl of QuickStatusButtonComponent
export class StatelessStatusSelectorComponent extends Component {
  static propTypes = {
    className: T.string,
    labelClassName: T.string,
    disabled: T.bool,
    readOnly: T.bool,
    /**
     * Supports both an AngularDomain instance and a POJO containing an id and a collection
     **/
    model: T.object,
    /**
     * Called with 1 parameter, the new {String} status, when the status successfully updates
     **/
    onSelect: T.func,
    hideValue: T.bool,
    hideIcon: T.bool,

    collection: T.string,
    status: T.string,
    validStatuses: T.array,
  };

  static defaultProps = {
    className: '',
    labelClassName: '',
    hideValue: false,
    hideIcon: false,
    validStatuses: [],
  };

  static getDefaultState() {
    return {
      isSaving: false,
      options: null,
    };
  }

  static HAS_REJECTION_STATUS = 'has_rejection';

  static PROJECT_STATUSES = [
    'cancelled',
    'pending',
    'in_progress',
    StatelessStatusSelectorComponent.HAS_REJECTION_STATUS,
    'complete',
  ];

  static DEFAULT_STATUSES = Object.values(TaskStatus);

  static $inject = [
    '$translate',
    '$modal',
    'fetchUtils',
  ];

  constructor(props) {
    super(props);
    this.state = StatelessStatusSelectorComponent.getDefaultState();

    const currentStatus = StatelessStatusSelectorComponent.getCurrentStatus(props, this.state);

    const init = StatelessStatusSelectorComponent.initializeStatuses(props.collection);
    const statusMap = StatelessStatusSelectorComponent.createStatusMap(props.validStatuses);
    this.state = {
      ...this.state,
      ...StatelessStatusSelectorComponent.getNextState(props, {
        STATUSES: init.statuses,
        currentStatus,
        statusMap,
        enforceStatus: init.enforceStatus,
      }),
      statusMap,
    };

    this.updateStatus = this.updateStatus.bind(this);
  }

  withNextStatus(props) {
    const status = props.status;
    if (this.status !== status && this.status$) {
      this.status = status;
      this.status$.next(status);
    }
  }

  // TODO: only update things if there is a change
  componentWillReceiveProps(nextProps) {
    if (!statusPropsAreEqual(this.props, nextProps)) {
      const statusMap = this.props.validStatuses !== nextProps.validStatuses
        ? StatelessStatusSelectorComponent.createStatusMap(nextProps.validStatuses)
        : this.state.statusMap;

      const currentStatus = StatelessStatusSelectorComponent.getCurrentStatus(nextProps, this.state);
      const init = StatelessStatusSelectorComponent.initializeStatuses(nextProps.collection);
      this.setState({
        ...StatelessStatusSelectorComponent.getNextState(nextProps, {
          STATUSES: init.statuses,
          currentStatus,
          statusMap,
          enforceStatus: init.enforceStatus,
        }),
      });
    }

    this.withNextStatus(nextProps);
  }

  componentDidMount() {
    this.status$ = new ReplaySubject(1);
    this.withNextStatus(this.props);
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    this.status$ = null;
    this.updateSubscription && this.updateSubscription.unsubscribe();
    this.stateSubscription && this.stateSubscription.unsubscribe();
    this.afterUpdateSubscription && this.afterUpdateSubscription.unsubscribe();
    this.afterUpdateSubscription = null;
  }

  render() {
    return (
      <PopoverSelectComponent
        className={classNames('StatusSelector', this.props.className)}
        labelClassName={classNames('StatusSelector__label', this.props.labelClassName)}
        model={StatelessStatusSelectorComponent.getCurrentStatus(this.props, this.state)}
        isSaving={this.state.isSaving}
        onSelect={this.updateStatus}
        options={this.state.options}
        showText={!this.props.hideValue}
        showIcon={!this.props.hideIcon}
        disabled={this.props.validStatuses.length < 2}
        readOnly={this.props.readOnly}
      />
    );
  }

  updateStatus(status) {
    const currentStatus = this.props.status;
    if (currentStatus === status)
      return;

    const domainClass = getDomainClass(this.props.model);
    this.setState({isSaving: true, status});
    return status === 'rejected'
      ? StatelessStatusSelectorComponent.openRejectionDialog()
        .then(comment =>
            domainClass.updateStatus(this.props.model, status, comment)
              .then(() => this.afterUpdate(currentStatus, status))
              .catch(this.onUpdateError.bind(this)),
          /* On cancel, reset state */
          () => this.afterUpdate(currentStatus, currentStatus))
      : domainClass.updateStatus(this.props.model, status)
        .then(() => this.afterUpdate(currentStatus, status))
        .catch(this.onUpdateError.bind(this));
  }

  /**
   * @param {String} currentStatus
   * @param {String} newStatus
   */
  afterUpdate(currentStatus, newStatus) {
    if (this.afterUpdateSubscription) {
      this.afterUpdateSubscription.unsubscribe();
      this.afterUpdateSubscription = null;
    }
    if (this.status$) {
      if (currentStatus !== newStatus) {
        this.afterUpdateSubscription = this.status$
          .distinctUntilChanged()
          .filter(status => status !== currentStatus)
          .delay(0)
          .subscribe(() => {
            this.setState({
              status: this.props.status,
              isSaving: false,
            });
            this.afterUpdateSubscription = null;
          });
      } else {
        this.setState({
          status: this.props.status,
          isSaving: false,
        });
      }
    }

    if (currentStatus !== status && isFunction(this.props.onSelect)) {
      this.props.onSelect(newStatus);
    }
  }

  /**
   * @param {Error} error
   */
  onUpdateError(error) {
    if (!this.isUnmounted) {
      this.setState({
        status: this.props.status,
        isSaving: false,
      });
    }
    StatelessStatusSelectorComponent.dependencies.fetchUtils.notificationErrorHandler(error);
  }

  static getStatusLabel = memoize(status => `status.${status}.label`);

  static getCurrentStatus(props, state) {
    return state.isSaving ? state.status : props.status;
  }

  static createStatusMap(validStatuses) {
    return validStatuses.reduce((statusMap, status) => {
      statusMap[status] = true;
      return statusMap;
    }, {});
  }

  static openRejectionDialog() {
    return StatelessStatusSelectorComponent.dependencies.$modal.show({
      template: statusRejectionTemplate,
      controller: 'StatusRejectionDialogController'
    });
  }

  /**
   * @typedef {Object} StatelessStatusState
   * @property {Array<String>} STATUSES
   * @property {String} currentStatus
   * @property {Object<String, Boolean>} statusMap
   * @property {Boolean} enforceStatus
   */

  /**
   * @param props
   * @param {StatelessStatusState} data
   * @return {{status: ?String, statuses: Array, options: *}}
   */
  static getNextState(props, data) {
    const statuses = StatelessStatusSelectorComponent.createStatusOptions(data.STATUSES, data.currentStatus);
    const optionClass = StatelessStatusSelectorComponent.getOptionClasses(data.STATUSES, data.currentStatus, data.statusMap);
    const nextState = {
      status: data.currentStatus,
    };
    nextState.statuses = statuses.map(item => Object.assign({
        disabled: !data.statusMap[item.value],
        className: optionClass[item.value],
        icon: getStatusIcon(item.value, item.value === data.currentStatus),
      },
      item,
    ));
    nextState.options = StatelessStatusSelectorComponent.buildOptions(nextState.statuses);
    return nextState;
  }

  /**
   *
   * @param {Array<String>} STATUSES
   * @param {String} currentStatus
   * @param {Object<String, Boolean>} validStatuses
   */
  static getOptionClasses(STATUSES, currentStatus, validStatuses) {
    return STATUSES.reduce((classes, status) => {
      classes[status] = `selector-item-status-${status}${(currentStatus === status ? '-active' : '')}`;
      if (!validStatuses[status])
        classes[status] += ' selector-item-status-disabled';
      return classes;
    }, {});
  }

  /**
   *
   * @param {Array<String>} statuses
   * @param {String} currentStatus
   * @return {Array<PartialOption>}
   */
  static createStatusOptions(statuses, currentStatus) {
    return statuses.map(status =>
      StatelessStatusSelectorComponent.createStatusOption(status, currentStatus)
    ).filter(it => it !== null);
  }

  /**
   *
   * @param {String} status
   * @param {String} currentStatus
   * @return {PartialOption}
   */
  static createStatusOption(status, currentStatus) {
    return status === StatelessStatusSelectorComponent.HAS_REJECTION_STATUS
      ? StatelessStatusSelectorComponent.getHasRejectionOption(currentStatus, status)
      : StatelessStatusSelectorComponent.getStatusOption(status);
  }

  /**
   * @typedef {Object} PartialOption
   * @property {String} value
   * @property {String} label
   */

  /**
   *
   * @param {String} currentStatus
   * @param {String} status
   * @return {PartialOption}
   */
  static getHasRejectionOption(currentStatus, status) {
    return currentStatus !== StatelessStatusSelectorComponent.HAS_REJECTION_STATUS ? null : {
      value: StatelessStatusSelectorComponent.HAS_REJECTION_STATUS,
      label: StatelessStatusSelectorComponent.dependencies.$translate.instant(StatelessStatusSelectorComponent.getStatusLabel(status))
    };
  }

  /**
   *
   * @param {String} status
   * @return {PartialOption}
   */
  static getStatusOption(status) {
    return {
      value: status,
      label: StatelessStatusSelectorComponent.dependencies.$translate.instant(StatelessStatusSelectorComponent.getStatusLabel(status))
    };
  }

  /**
   * @typedef {Object} StatusSet
   * @property {Boolean} enforceStatus If true, Metric data is used to enforce selectable statuses
   * @property {Array<String>} statuses List of selectable statuses
   */

  /**
   *
   * @param {String} collection
   * @return {StatusSet}
   */
  static initializeStatuses(collection) {
    switch (collection) {
      case PROJECT_COLLECTION:
      case ASSET_PROJECT_COLLECTION:
        return {
          enforceStatus: false,
          statuses: StatelessStatusSelectorComponent.PROJECT_STATUSES,
        };

      default:
        return {
          enforceStatus: true,
          statuses: StatelessStatusSelectorComponent.DEFAULT_STATUSES,
        };
    }
  }

  static buildOptions(statuses) {
    const optionItems = [];
    const selectValueMap = {};

    for (let i = 0, optionValuesLength = statuses.length; i < optionValuesLength; i++) {
      const viewValue = statuses[i].value;
      const selectValue = statuses[i].value;
      const label = statuses[i].label;
      const group = null;
      const disabled = statuses[i].disabled;
      const className = statuses[i].className;
      const icon = statuses[i].icon;
      const optionItem = new SelectOption(selectValue, viewValue, label, group, disabled, className, icon);

      optionItems.push(optionItem);
      selectValueMap[selectValue] = optionItem;
    }

    return PopoverSelectComponent.buildDefaultOptions(optionItems, selectValueMap);
  }
}

export function statusPropsAreEqual(a, b) {
  if (!a && !b) {
    return true;
  } else if (!a || !b) {
    return false;
  } else {
    return (
      getId(a.model) === getId(b.model)
      && a.status === b.status
      && a.validStatuses === b.validStatuses
    );
  }
}



// WEBPACK FOOTER //
// ./src/js/core-components/selectors/components/StatelessStatusSelector/StatelessStatusSelectorComponent.js